import { NgForOf, NgIf } from '@angular/common';
import { Component, computed, Inject, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MatCard,
  MatCardActions,
  MatCardContent,
  MatCardHeader,
  MatCardImage,
  MatCardSubtitle,
  MatCardTitle,
} from '@angular/material/card';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { sortBy } from 'lodash';
import { filter, startWith } from 'rxjs';
import { EWidget } from 'scw-digital-boards-widgets';
import { TReducerState } from '../../../app.config';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { environment } from '../../../core/environments/environment';
import { WorkspaceModuleService } from '../../../core/services/workspace-module.service';
import { IWorkspaceModules } from '../../settings/dashboards/dashboards.interface';
import { addWidgetConfiguration } from '../dashboard-constants.class';
import { EDashboardLayout, IWidgetData } from '../dashboard-content.interface';
import { WidgetAddEditDialogComponent } from '../widget-add-edit-dialog/widget-add-edit-dialog.component';
import { IAddEditDialogInjectData } from '../widget-add-edit-dialog/widget-add-edit-dialog.interface';
import { ICatalogFilter, ICatalogItemData } from './add-widget-catalog-dialog.interface';
import { WidgetCatalogItemComponent } from './widget-catalog-item/widget-catalog-item.component';

@Component({
  imports: [
    MatDialogTitle,
    TranslatePipe,
    MatDialogContent,
    MatChipListbox,
    MatChipOption,
    ReactiveFormsModule,
    WidgetCatalogItemComponent,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatSuffix,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatCardContent,
    MatCardActions,
    MatCard,
    MatCardImage,
    NgForOf,
    NgIf,
  ],
  selector: 'app-add-widget-catalog-dialog',
  styleUrl: './add-widget-catalog-dialog.component.scss',
  templateUrl: './add-widget-catalog-dialog.component.html',
})
export class AddWidgetCatalogDialogComponent {
  private readonly translate = inject(TranslateService);
  public readonly workspaceModuleService = inject(WorkspaceModuleService);

  public readonly searchFilterFormControl = new FormControl<string>('', { nonNullable: true });
  public readonly chipFilterFormControl = new FormControl<(keyof IWorkspaceModules)[]>([], { nonNullable: true });

  private readonly appliedChipFilters = toSignal(this.chipFilterFormControl.valueChanges, {
    initialValue: this.chipFilterFormControl.value,
  });

  private readonly appliedSearchFilter = toSignal(
    this.searchFilterFormControl.valueChanges.pipe(startWith(this.searchFilterFormControl.value)),
  );

  private readonly workspaceModulePermissions = this.globalStore.selectSignal(
    (state) => state.dashboardContent.dashboardData?.workspace.modulePermissions,
  );

  public readonly widgetData: Signal<ICatalogItemData[]> = computed(() => {
    const data = Object.entries(addWidgetConfiguration).map(([widgetType, config]) => {
      const workspaceModulePermissions = this.workspaceModulePermissions();

      return {
        description: this.translate.instant(addWidgetConfiguration[widgetType as EWidget].descriptionTranslateKey),
        firstModuleWithoutPermission:
          (config.relatedModules.find((module) => !workspaceModulePermissions?.[module]) as
            | keyof IWorkspaceModules
            | undefined) ?? null,
        relatedModules: config.relatedModules,
        type: widgetType as EWidget,
        widgetName: this.translate.instant(`widget.${widgetType}.name`),
      };
    });

    return sortBy(data, 'widgetName');
  });

  public readonly filters: readonly ICatalogFilter[] = [
    {
      numberOfWidgets: this.widgetData().filter(({ relatedModules }) => relatedModules.includes('oeeTracker')).length,
      value: 'oeeTracker',
    },
    {
      numberOfWidgets: this.widgetData().filter(({ relatedModules }) => relatedModules.includes('laborTracker')).length,
      value: 'laborTracker',
    },
    {
      numberOfWidgets: this.widgetData().filter(({ relatedModules }) => relatedModules.includes('actionTracker'))
        .length,
      value: 'actionTracker',
    },
    {
      numberOfWidgets: this.widgetData().filter(({ relatedModules }) => relatedModules.includes('scheduler')).length,
      value: 'scheduler',
    },
    {
      numberOfWidgets: this.widgetData().filter(({ relatedModules }) => relatedModules.includes('utilityWidgets'))
        .length,
      value: 'utilityWidgets',
    },
  ];

  public readonly catalogItems = computed(() => {
    const appliedChipFilters = this.appliedChipFilters();
    const appliedSearchFilter = this.appliedSearchFilter()?.trim().toLowerCase();

    return Object.values(this.widgetData()).filter(
      (data: ICatalogItemData) =>
        (!appliedChipFilters.length ||
          appliedChipFilters.some((appliedFilter) => data.relatedModules.includes(appliedFilter))) &&
        (!appliedSearchFilter ||
          data.widgetName.toLowerCase().includes(appliedSearchFilter) ||
          data.description.toLowerCase().includes(appliedSearchFilter)),
    );
  });

  protected readonly environment = environment;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly injectData: { layout: EDashboardLayout },
    private readonly matDialog: MatDialog,
    private readonly dialogRef: MatDialogRef<IWidgetData>,
    private readonly globalStore: Store<TReducerState>,
  ) {}

  public openAddModal(widgetType: EWidget): void {
    this.matDialog
      .open(WidgetAddEditDialogComponent, {
        closeOnNavigation: true,
        data: { editData: null, layout: this.injectData.layout, type: widgetType } as IAddEditDialogInjectData,
        disableClose: true,
        exitAnimationDuration: 0,
        maxWidth: '100%',
        width: EDialogWidth.medium,
      })
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe((widget: IWidgetData) => {
        this.dialogRef.close(widget);
      });
  }
}
